import {
  ChangeEvent,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Link from "next/link";
import { t, Trans } from "@lingui/macro";
import { AlertBanner, Heading, Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import StickyBox from "react-sticky-box";
import { useSession } from "next-auth/client";
import { AppCtx } from "src/contexts/app.context";
import { googleTagManager } from "src/tracking/googleTagManager";
import { CartData, useCart } from "src/hooks/useCart";
import { useSegment } from "src/hooks/useSegment";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { getGenderId, setGender } from "src/modules/page";
import { Button } from "src/atoms/Button";
import {
  getIsDiscountCodeInLineItems,
  getLineItemsDiscountPrice,
} from "src/utils/ct/discountCodeHelper";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import { CartItem } from "src/molecules/CartItem";
import { CartViewSkeleton } from "src/molecules/CartViewSkeleton";
import { PageHead } from "src/molecules/PageHead";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import {
  ButtonDeleteAllUnavailableItems,
  CartHeader,
  CartItems,
  CartViewCarouselsWrapper,
  CartViewContent,
  CartViewOrderSummaryWrapper,
} from "./CartView.styled";
import CTPrice from "src/atoms/CTPrice/CTPrice";
import { DiscountCodeState, LineItem, Reference } from "src/types/ctgraphql.d";
import { useAddDiscountCodeToCart } from "src/hooks/useAddDiscountCodeToCart";
import { useRemoveDiscountCodeFromCart } from "src/hooks/useRemoveDiscountCodeFromCart";
import { useRecalculateCart } from "src/hooks/useRecalculateCart";
import { cartCountHelper } from "src/utils/ct/cartCountHelper";
import { useDeleteCartItem } from "src/hooks/useDeleteCartItem";
import { getCartItemAvailability } from "src/utils/ct/cartItemAvailabilityHelper";
import { useCreateCart } from "src/hooks/useCreateCart";
import { ButtonCheckout } from "./ButtonCheckout";
import { CartNotificationModal } from "./CartNotificationModal";
import { getErrorMessage } from "./utils";
import { ExpressCheckout } from "src/molecules/ExpressCheckout";
import { PaymentMethod, ProductGender } from "src/types/graphql.d";
import { CartWarning } from "src/atoms/CartWarning";
import CartViewCarousels from "src/organisms/CartView/CartViewCarousels";
import CartViewOrderSummary from "src/organisms/CartView/CartViewOrderSummary";
import { useLingui } from "@lingui/react";
import couponWarningsTranslation from "src/translations/couponWarnings";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { usePspMollieEnabled } from "src/hooks/usePspMollieEnabled";

interface BottomCheckoutButtonProps {
  disabled: boolean;
  onClick?: () => void;
}

const BottomCheckoutButton: FC<BottomCheckoutButtonProps> = ({
  disabled,
  onClick,
}) => {
  return (
    <Box
      pt={[3, 3, 4]}
      pb={[3, 3, "space48"]}
      px={[3, 3, 0]}
      width="100%"
      backgroundColor="White"
      sx={{
        position: ["absolute", "absolute", "initial"],
        bottom: [0, 0, "initial"],
      }}
    >
      <ButtonCheckout
        dataTestid="cart-continue-to-checkout-bottom-a"
        disabled={disabled}
        onClick={onClick}
      />
    </Box>
  );
};

const CartView = (): JSX.Element => {
  const { _, i18n } = useLingui();
  const {
    locale,
    isPrivateSale,
    cartInfo,
    storeConfig,
    cartMinLineItemsTotalPriceDiff,
    featureFlags,
    setPageViewedProperties,
  } = useContext(AppCtx);

  const [showCartError, setShowCartError] = useState<boolean>(false);

  const [couponInput, setCouponInput] = useState("");
  const [couponWarnings, setCouponWarnings] = useState<string[]>([]);
  const [analyticsTracked, setAnalyticsTracked] = useState(false);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string>("");
  const [isCartRecalculating, setIsCartRecalculating] =
    useState<boolean>(false);
  const [isDiscountCodeRemoving, setIsDiscountCodeRemoving] =
    useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const genderId = useSelector(getGenderId);

  const [session] = useSession();
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoints();
  const isMollieEnabled = usePspMollieEnabled();
  const {
    segmentCartViewed,
    segmentCouponApplied,
    segmentCouponDenied,
    segmentCouponEntered,
    segmentCouponRemoved,
  } = useSegment();

  useEffect(() => {
    setPageViewedProperties({
      page_type: "cart",
    });
  }, [setPageViewedProperties]);

  useEffect(() => {
    if (!genderId) {
      dispatch(setGender("women"));
    }
  }, [dispatch, genderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data } = useCart({
    onCompleted: (cartData: CartData) => {
      if (cartData?.inStore?.me?.activeCart && !analyticsTracked) {
        setAnalyticsTracked(true);

        void segmentCartViewed(cartData.inStore.me.activeCart);

        if (session?.user?.email) {
          googleTagManager.trackCartPage(
            session.user.email,
            cartData?.inStore?.me?.activeCart.lineItems
          );
        }
      }
    },
  });

  const { loading: loadingRecalculateCart, recalculateCart } =
    useRecalculateCart({
      refetchQueries: ["cart"],
    });

  const { createCart } = useCreateCart({
    refetchQueries: ["cart"],
  });

  useEffect(() => {
    if (data?.cart === null) {
      void createCart({
        currency: storeConfig.currencyCode,
      });
    }
  }, [data, storeConfig.currencyCode, createCart]);

  const { addDiscountCodeToCart, loading: addDiscountCodeToCartLoading } =
    useAddDiscountCodeToCart({
      refetchQueries: ["cart"],
      onCompleted: ({ updateMyCart: cart }) => {
        if (cart) {
          cart.discountCodes.forEach((discountCode) => {
            if (discountCode.state === DiscountCodeState.MatchesCart) {
              const { taxedPrice, totalPrice, lineItems, discountCodes } = cart;
              const discountAbs = getLineItemsDiscountPrice(
                discountCodes,
                lineItems
              );

              void segmentCouponApplied({
                couponID: discountCode.discountCodeRef.id,
                cartTotalProducts: cartCountHelper(lineItems),
                cartValue:
                  (taxedPrice?.totalGross || totalPrice)?.centAmount / 100,
                couponCode: couponInput,
                couponDiscountAbsolute: discountAbs
                  ? formatStandardPrice(discountAbs).toString()
                  : "",
                currency: totalPrice?.currencyCode as string,
              });
              setCouponInput("");
            }
          });
        }
      },
      onError: (error) => {
        const warningMessage = getErrorMessage(error);
        const translatedWarning =
          typeof warningMessage === "string"
            ? warningMessage
            : _(warningMessage);
        setCouponWarnings([translatedWarning]);

        const [err] = error.graphQLErrors;
        if (data?.cart) {
          const { taxedPrice, totalPrice, lineItems } = data.cart;
          void segmentCouponDenied({
            couponCode: couponInput,
            cartTotalProducts: cartCountHelper(lineItems),
            cartValue: (taxedPrice?.totalGross || totalPrice)?.centAmount / 100,
            couponErrorCode: (err as { code?: string }).code,
            couponDenied: err.message,
            currency: totalPrice?.currencyCode as string,
          });
        }
      },
    });

  const {
    removeDiscountCodeFromCart,
    loading: removeDiscountCodeFromCartLoading,
  } = useRemoveDiscountCodeFromCart({
    onCompleted: () => {
      setIsDiscountCodeRemoving(false);
    },
    onError: (error) => {
      setCouponWarnings(
        error.graphQLErrors.map((graphQLError) => graphQLError.message)
      );
    },
  });

  function onRemoveUnavailableItemsClick(lineItemIds: string[]): void {
    void deleteCartItem(lineItemIds);
  }

  const { deleteCartItem } = useDeleteCartItem({
    refetchQueries: ["cart"],
    onCompleted: () => {
      setItemsOutOfStock([]);
    },
    onError: (error) => {
      setCouponWarnings(
        error.graphQLErrors.map((graphQLError) => graphQLError.message)
      );
    },
  });

  useEffect(() => {
    if (data?.cart && cartInfo.id && cartInfo.version && !isCartRecalculating) {
      recalculateCart();
      setIsCartRecalculating(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.cart, cartInfo, isCartRecalculating]);

  useEffect(() => {
    if (data.cart && !isDiscountCodeRemoving) {
      data.cart.discountCodes.forEach((discountCode) => {
        const isCartLineItemsDiscount =
          discountCode.discountCode?.cartDiscounts.find(
            ({ target }) => target?.type !== "shipping"
          );
        const isCartLineItemsDiscountValue =
          isCartLineItemsDiscount &&
          data.cart?.lineItems &&
          getIsDiscountCodeInLineItems(data.cart.lineItems, discountCode);

        const isCartShippingDiscount =
          discountCode.discountCode?.cartDiscounts.find(
            ({ target }) => target?.type === "shipping"
          );
        const isCartShippingDiscountValue =
          isCartShippingDiscount && data.cart?.shippingInfo?.discountedPrice;

        if (discountCode.state === DiscountCodeState.MatchesCart) {
          setAppliedDiscountCode(
            discountCode.discountCode?.name ||
              discountCode.discountCode?.code ||
              ""
          );
        }

        if (
          discountCode.state !== DiscountCodeState.MatchesCart ||
          ((!isCartLineItemsDiscount || !isCartLineItemsDiscountValue) &&
            (!isCartShippingDiscount || !isCartShippingDiscountValue))
        ) {
          setIsDiscountCodeRemoving(true);
          removeDiscountCodeFromCart(discountCode.discountCodeRef);
          const translatedWarning = _(
            couponWarningsTranslation[
              "The coupon is not applicable to your cart."
            ]
          );
          setCouponWarnings([translatedWarning]);
        }
      });
    }
  }, [
    data?.cart,
    removeDiscountCodeFromCart,
    isDiscountCodeRemoving,
    setIsDiscountCodeRemoving,
    _,
  ]);

  const onCouponInputUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    setCouponInput(event.target.value.toUpperCase());
    setCouponWarnings([]);
  };

  const onAddDisountCodeToCart = () => {
    setIsDiscountCodeRemoving(false);
    setCouponWarnings([]);
    addDiscountCodeToCart(couponInput);
    if (data?.cart) {
      const { taxedPrice, totalPrice, lineItems } = data.cart;
      void segmentCouponEntered({
        couponCode: couponInput,
        currency: totalPrice?.currencyCode as string,
        cartValue: (taxedPrice?.totalGross || totalPrice)?.centAmount / 100,
        cartTotalProducts: cartCountHelper(lineItems),
      });
    }
  };

  function onCouponInputEnter(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      onAddDisountCodeToCart();
    }
  }

  const onRemoveDisountCodeFromCart = (code?: Reference) => {
    if (code) {
      removeDiscountCodeFromCart(code);

      if (data?.cart) {
        const { taxedPrice, totalPrice, lineItems } = data.cart;
        void segmentCouponRemoved({
          couponID: code.id,
          couponCode: couponInput,
          currency: totalPrice?.currencyCode as string,
          cartValue: (taxedPrice?.totalGross || totalPrice)?.centAmount / 100,
          cartTotalProducts: cartCountHelper(lineItems),
        });
      }
    }
  };
  const [itemsOutOfStock, setItemsOutOfStock] = useState<string[]>([]);
  const isDisabled =
    addDiscountCodeToCartLoading || removeDiscountCodeFromCartLoading;
  const isEmptyCart = !data?.cart?.id || !data?.cart?.lineItems?.length;
  const isEmptyCartAndNoItemWarnings = isEmptyCart;

  useEffect(() => {
    if (data?.cart?.lineItems) {
      const outOfStockIds: string[] = data?.cart?.lineItems
        ?.filter((item: LineItem) => {
          const { isItemOutOfStock } = getCartItemAvailability(item);
          return isItemOutOfStock;
        })
        .map((obj: LineItem) => obj.id);

      setItemsOutOfStock((prevArray: string[]) => [
        ...new Set([...prevArray, ...outOfStockIds]),
      ]);
    }
  }, [data?.cart?.lineItems]);

  const isLoading: boolean =
    !data?.cart || !session?.accessToken || loadingRecalculateCart;

  const handleExpressCheckoutError = useCallback((error?: unknown) => {
    setShowCartError(!!error);
  }, []);

  const expressCheckoutEnabled = useMemo(
    () =>
      featureFlags.expressCheckout &&
      data.cart &&
      itemsOutOfStock?.length === 0 &&
      formatStandardPrice(
        data.cart.taxedPrice?.totalGross || data.cart.totalPrice
      ) > 0,
    [data.cart, featureFlags.expressCheckout, itemsOutOfStock?.length]
  );

  const unsellableItems = useMemo(
    () =>
      data.cart?.lineItems
        .filter((item) =>
          item.variant?.attributesRaw.some(
            (attr) => attr.name === "sellable" && !attr.value
          )
        )
        .map((item) => item.id) || [],

    [data?.cart?.lineItems]
  );

  const hasUnsellableItems = useMemo(
    () => (unsellableItems ? unsellableItems.length > 0 : false),
    [unsellableItems]
  );

  const unsellableItemsIds = useMemo(() => {
    return [...new Set([...itemsOutOfStock, ...unsellableItems])];
  }, [itemsOutOfStock, unsellableItems]);

  let alertBanner = t(i18n)`Products in your shopping cart are not reserved`;

  const { cartAlertBanner } = useFeatureFlags();
  if (cartAlertBanner && (locale === "en" || locale === "nl")) {
    alertBanner =
      (Object.entries(cartAlertBanner).find(
        ([key]) => key === locale
      )?.[1] as string) || "";
  }

  return (
    <Box>
      <PageHead title={t(i18n)`Cart | Otrium`} />
      {isLoading ? (
        <CartViewSkeleton />
      ) : (
        <>
          <CartViewContent as="main">
            <CartHeader>
              <Box pt="48px" sx={{ position: "relative" }}>
                <Box sx={{ textAlign: "center" }} as="header">
                  <Heading
                    as="h1"
                    mb={3}
                    color="tone.black"
                    fontSize={[4, 4, 4, 5]}
                    fontFamily="PlusJakartaSans"
                    fontWeight="bold"
                    lineHeight={[1.25, 1.25, 1.25, 1.33]}
                  >
                    <Trans>Shopping cart</Trans>
                  </Heading>

                  <Flex mt={3} mx={[3, 3, 3, 0]} justifyContent="center">
                    {isEmptyCartAndNoItemWarnings ? (
                      <Text color="tone.black">
                        <Trans
                          render={({ translation }) => (
                            <p style={{ display: "inline-block" }}>
                              {translation}
                            </p>
                          )}
                        >
                          It looks like your shopping cart is still empty
                        </Trans>
                      </Text>
                    ) : (
                      <Box sx={{ textAlign: "left" }}>
                        {cartMinLineItemsTotalPriceDiff &&
                        cartMinLineItemsTotalPriceDiff.centAmount > 0 ? (
                          <AlertBanner variant="info">
                            <Text
                              variant="body.source.semibold14"
                              letterSpacing={0}
                            >
                              <Trans id="cart.min_cart_subtotal_warning">
                                Add another{" "}
                                <CTPrice
                                  price={cartMinLineItemsTotalPriceDiff}
                                />{" "}
                                to reach the minimum order value, the items in
                                your cart are selling fast!
                              </Trans>
                            </Text>
                          </AlertBanner>
                        ) : (
                          alertBanner &&
                          alertBanner !== "" && (
                            <AlertBanner variant="info">
                              <Text
                                variant="body.source.semibold14"
                                letterSpacing={0}
                              >
                                {alertBanner}
                              </Text>
                            </AlertBanner>
                          )
                        )}
                      </Box>
                    )}
                  </Flex>
                </Box>

                <Flex
                  {...(isEmptyCartAndNoItemWarnings
                    ? { mt: ["96px", "96px", "96px", 3] }
                    : { mt: [3] })}
                  mx={[3, 3, 3, 0]}
                  justifyContent="center"
                  flexDirection={["column", "column", "column", "row", "row"]}
                >
                  {isEmptyCartAndNoItemWarnings ? (
                    <Box mt={4} width={["auto", "auto", "auto", 248]}>
                      <Link
                        href={getI18nRoute({
                          route: `/${genderId as string}`,
                          locale,
                        })}
                        passHref
                        data-testid="cart-start-shopping-a"
                      >
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}

                        <Button
                          data-testid="cart-start-shopping-button"
                          variant="primary"
                          fullWidth
                        >
                          <Trans>Start shopping</Trans>
                        </Button>
                      </Link>
                    </Box>
                  ) : (
                    <>
                      {!expressCheckoutEnabled && !isPrivateSale && (
                        <Box
                          width={[
                            "auto",
                            "auto",
                            "auto",
                            isEmptyCart ? 320 : "50%",
                          ]}
                        >
                          <Link
                            href={`${getI18nRoute({
                              route: `/${genderId as string}`,
                              locale,
                            })}/categories`}
                            data-testid="cart-continue-shopping-a"
                          >
                            <Button
                              data-testid="cart-continue-shopping-button"
                              variant="secondary"
                              fullWidth
                            >
                              <Trans>Continue shopping</Trans>
                            </Button>
                          </Link>
                        </Box>
                      )}

                      {!isEmptyCart && (
                        <Box
                          mt={[2, 2, 2, 0]}
                          ml={[0, 0, 0, "24px"]}
                          width={["auto", "auto", "auto", "50%"]}
                        >
                          <ButtonCheckout
                            variant="primary"
                            dataTestid="cart-continue-to-checkout-top-a"
                            disabled={
                              itemsOutOfStock?.length > 0 || hasUnsellableItems
                            }
                            onClick={() => setIsOpen(true)}
                          />
                        </Box>
                      )}
                      {expressCheckoutEnabled &&
                        !isMollieEnabled &&
                        data.cart && (
                          <ExpressCheckout
                            cart={data.cart}
                            activePaymentMethods={[PaymentMethod.GooglePay]}
                            onError={handleExpressCheckoutError}
                          />
                        )}
                    </>
                  )}
                </Flex>

                {showCartError && (
                  <Box
                    {...(isEmptyCartAndNoItemWarnings
                      ? { mt: ["96px", "96px", "96px", 3] }
                      : { mt: [3] })}
                    mx={[3, 3, 3, 0]}
                  >
                    <CartWarning
                      warning={<Trans>express.checkout.error</Trans>}
                    />
                  </Box>
                )}
              </Box>
            </CartHeader>

            <Flex
              flexWrap="wrap"
              justifyContent="center"
              flexDirection={["column", "column", "column", "row"]}
            >
              {!isEmptyCart && (
                <CartItems
                  mt={[5, 5, 5, "40px"]}
                  mr={[0, 0, 3, 4]}
                  ml={[0, 0, 3, 0]}
                >
                  <Box>
                    {data?.cart?.lineItems?.map((item) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        discountCode={appliedDiscountCode}
                      />
                    ))}
                  </Box>
                  {!isEmptyCart &&
                  (itemsOutOfStock.length > 0 || hasUnsellableItems) ? (
                    <Box my={3} sx={{ textAlign: "center" }}>
                      <ButtonDeleteAllUnavailableItems
                        data-testid="cart-remove-all-unavailable-items-button"
                        onPress={() =>
                          onRemoveUnavailableItemsClick(unsellableItemsIds)
                        }
                      >
                        <Text
                          color="tone.black"
                          lineHeight="1.5"
                          letterSpacing="0.4px"
                          fontWeight="600"
                          sx={{
                            textDecoration: "underline",
                            ":hover": {
                              textDecoration: "none",
                            },
                          }}
                        >
                          <Trans>Remove all unavailable items</Trans>
                        </Text>
                      </ButtonDeleteAllUnavailableItems>
                    </Box>
                  ) : null}
                </CartItems>
              )}
              {isMobile && genderId && (
                <CartViewCarouselsWrapper>
                  <CartViewCarousels shopType={genderId as ProductGender} />
                </CartViewCarouselsWrapper>
              )}
              {!isEmptyCart && (
                <CartViewOrderSummaryWrapper
                  data-testid="cart-order-summary"
                  mt={[5, 5, 5, "40px"]}
                  mb={["96px", "96px", 0]}
                >
                  {isMobile ? (
                    <CartViewOrderSummary
                      isDisabled={isDisabled}
                      cart={data.cart}
                      onRemoveDiscountCodeFromCart={onRemoveDisountCodeFromCart}
                      couponInput={couponInput}
                      couponWarnings={couponWarnings}
                      addDiscountCodeToCartLoading={
                        addDiscountCodeToCartLoading
                      }
                      onAddDiscountCodeToCart={onAddDisountCodeToCart}
                      onCouponInputUpdate={onCouponInputUpdate}
                      onCouponInputEnter={onCouponInputEnter}
                      hasOutOfStockItems={
                        itemsOutOfStock?.length > 0 || hasUnsellableItems
                      }
                      setIsOpen={setIsOpen}
                      isEmptyCart={isEmptyCart}
                    />
                  ) : (
                    <StickyBox offsetTop={100}>
                      <CartViewOrderSummary
                        isDisabled={isDisabled}
                        cart={data.cart}
                        onRemoveDiscountCodeFromCart={
                          onRemoveDisountCodeFromCart
                        }
                        couponInput={couponInput}
                        couponWarnings={couponWarnings}
                        addDiscountCodeToCartLoading={
                          addDiscountCodeToCartLoading
                        }
                        onAddDiscountCodeToCart={onAddDisountCodeToCart}
                        onCouponInputUpdate={onCouponInputUpdate}
                        onCouponInputEnter={onCouponInputEnter}
                        hasOutOfStockItems={
                          itemsOutOfStock?.length > 0 || hasUnsellableItems
                        }
                        setIsOpen={setIsOpen}
                        isEmptyCart={isEmptyCart}
                      />
                    </StickyBox>
                  )}
                </CartViewOrderSummaryWrapper>
              )}
              {!isMobile && genderId && (
                <CartViewCarouselsWrapper mt={5} mb={6}>
                  <CartViewCarousels shopType={genderId as ProductGender} />
                </CartViewCarouselsWrapper>
              )}
            </Flex>
            {isMobile && !isEmptyCart && (
              <StickyBox bottom={true}>
                <BottomCheckoutButton
                  disabled={itemsOutOfStock?.length > 0 || hasUnsellableItems}
                  onClick={() => setIsOpen(true)}
                />
              </StickyBox>
            )}
          </CartViewContent>

          {!isEmptyCart && !isMobile && <RRPFootnote />}

          {isOpen && (
            <CartNotificationModal
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CartView;
